// @refresh reload
import { mount, StartClient } from "@solidjs/start/client";
import { dispatchGlobalEvent } from "@storytell/events";
import "solid-devtools";
import { isSafeToReload, isStreamingNow, markUserActive, shouldForceReload } from "~/lib/appActivity";

// Must run before any cookieStorage reads to prevent URIError from malformed cookies
try {
  document.cookie = "prompt-history:v1=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = "prompt-draft:v1=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
} catch (_error) {
  /* expected -- cookie cleanup is best-effort */
}

// Solid's delegated handler defines a new currentTarget getter on every event. Chrome 154 (not 148)
// keeps the first getter defined per event class for the life of the page, and with it that event
// and the whole view it was dispatched in: ~7MB for a chat view, kept after navigating away.
// Defining the same accessor on one never-dispatched event per class takes that slot first, and no
// listener runs. The getter must close over its event, or V8 frees the slot once the event is
// collected and the next real event takes it. Delete once Solid shares one getter across events
// (1.9.17 still doesn't) or Chrome stops keeping it.
for (const e of [
  new PointerEvent("click"),
  new MouseEvent("mousedown"),
  new FocusEvent("focusin"),
  new KeyboardEvent("keydown"),
  new InputEvent("input"),
  new Event("input"),
  // Desktop Firefox has no TouchEvent constructor.
  ...("TouchEvent" in window ? [new TouchEvent("touchstart")] : []),
]) {
  Object.defineProperty(e, "currentTarget", { configurable: true, get: () => e.target ?? document });
}

const app = document.getElementById("app");
if (!app) throw new Error("entry-client: the #app mount element is missing");
mount(() => <StartClient />, app);

window.addEventListener("keydown", markUserActive, { passive: true });
window.addEventListener("pointerdown", markUserActive, { passive: true });
window.addEventListener("wheel", markUserActive, { passive: true });
window.addEventListener("touchmove", markUserActive, { passive: true });

let reloading = false;
let swUpdateNoticeShown = false;
let deferStartedAt = 0;
let retryTimer: ReturnType<typeof setInterval> | undefined;

// isSafeToReload() can flip to true purely from the passage of time (IDLE_MS),
// which fires no event, so poll as the backstop; visibilitychange only trims latency.
const RETRY_POLL_MS = 5000;

const stopRetry = () => {
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = undefined;
  }
  document.removeEventListener("visibilitychange", onTabHidden);
};

const onTabHidden = () => {
  if (document.hidden) deferReloadUntilSafe();
};

// A reload mid-chat destroys the in-progress prompt/stream, so route auto-reloads
// through this instead of reloading unconditionally. When unsafe, surface the
// existing NavBar "update available" button and retry until the tab is idle,
// hidden, or the defer ceiling forces recovery.
function deferReloadUntilSafe() {
  if (reloading) return;

  const now = Date.now();
  if (!isSafeToReload() && !shouldForceReload(deferStartedAt, now, isStreamingNow())) {
    if (!deferStartedAt) deferStartedAt = now;
    if (!swUpdateNoticeShown) {
      swUpdateNoticeShown = true;
      dispatchGlobalEvent("sw-update-available", undefined);
    }
    if (!retryTimer) {
      retryTimer = setInterval(deferReloadUntilSafe, RETRY_POLL_MS);
      document.addEventListener("visibilitychange", onTabHidden);
    }
    return;
  }

  reloading = true;
  stopRetry();
  dispatchGlobalEvent("flush-prompt-bar-draft", undefined);
  setTimeout(() => window.location.reload(), 500);
}

const isUnhandledImportRejection = (message: string) =>
  message.includes("Failed to fetch dynamically imported module") ||
  message.includes("Importing a module script failed") ||
  message.includes("Unexpected token '<'"); // server returned HTML instead of JS

window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  const message = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
  if (isUnhandledImportRejection(message)) {
    console.warn("Unhandled import rejection — reloading...");
    deferReloadUntilSafe();
  }
});
window.addEventListener("error", (event) => {
  if (isUnhandledImportRejection(event.message || "")) {
    console.warn("Detected missing build chunk — reloading...");
    deferReloadUntilSafe();
  }
});

if ("serviceWorker" in navigator) {
  // Track page load time and initial controller state to determine reload behavior.
  // We only want to reload for "stale tab" scenarios where:
  // 1. There was already a SW controlling the page (not first-time install)
  // 2. The page has been open for a while (not a fresh navigation that already has new code)
  const pageLoadTime = Date.now();
  const hadControllerOnLoad = !!navigator.serviceWorker.controller;

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      // Helper function to safely update the service worker
      const safeUpdate = () => {
        registration.update().catch(() => {
          // Silently handle update failures - they're expected during network issues
          // or when the service worker file is temporarily unavailable
        });
      };

      // Initial update check
      safeUpdate();

      // Check periodically for new deployments (only when tab is active)
      let updateInterval: ReturnType<typeof setInterval> | null = null;

      const startUpdateInterval = () => {
        if (updateInterval) return; // Already running
        updateInterval = setInterval(
          () => {
            // Only update if tab is visible
            if (document.visibilityState === "visible") {
              safeUpdate();
            }
          },
          3 * 60 * 1000
        );
      };

      const stopUpdateInterval = () => {
        if (updateInterval) {
          clearInterval(updateInterval);
          updateInterval = null;
        }
      };

      // Start interval if tab is currently visible
      if (document.visibilityState === "visible") {
        startUpdateInterval();
      }

      // On browser tab activation, check for SW updates and restart interval
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          safeUpdate();
          startUpdateInterval();
        } else {
          stopUpdateInterval();
        }
      });

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            dispatchGlobalEvent("sw-update-available", undefined);
          }
        });
      });

      if (registration.waiting && navigator.serviceWorker.controller) {
        dispatchGlobalEvent("sw-update-available", undefined);
      }

      // Reload when a new SW takes control, but only for "stale tab" scenarios.
      // - If controllerchange fires within 1 min of page load, it's a fresh navigation
      //   and the browser already fetched new code from the server. No reload needed.
      // - If controllerchange fires later, the tab was left open during a deployment
      //   and has stale in-memory code. Reload to get the new version.
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        const timeSincePageLoad = Date.now() - pageLoadTime;
        const isStaleTab = hadControllerOnLoad && timeSincePageLoad > 15 * 60 * 1000;

        if (isStaleTab) {
          console.log("New service worker activated on stale tab — reloading...");
          deferReloadUntilSafe();
        }
      });
    } catch {
      // Silently handle registration failures - service worker is optional
      // Users can still use the app without it
    }
  };
  void registerServiceWorker();
}
